import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Sparkles, Clock, ChevronRight, BookOpen } from "lucide-react";
import Header from "../components/Header";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Techniques — /techniques
 *
 * Face-scan-free mini-tutorials. Users who want to learn ONE thing
 * (smoky eye, cut crease, soft contour, etc.) without going through
 * a full face scan come here. 12 curated techniques as of launch.
 *
 * Free users see the gallery with every card locked (Pro overlay).
 * Pro users (or active 7-day trial) see fully unlocked cards.
 *
 * The lock-overlay is intentionally NOT a hard block — free users can
 * still see what they're missing, with a "Try free for 7 days" CTA on
 * each click. This is the same conversion pattern as Netflix's
 * "Up Next" rail or Spotify's blurred-track UI.
 */

const DIFFICULTY_COPY = {
  beginner: { label: "Beginner", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  intermediate: { label: "Intermediate", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  advanced: { label: "Advanced", chip: "bg-rose-50 text-rose-700 border-rose-200" },
};

const TechniqueGallery = () => {
  const navigate = useNavigate();
  const [techniques, setTechniques] = useState([]);
  const [pro, setPro] = useState(false);
  const [trialEligible, setTrialEligible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [gallery, usage] = await Promise.all([
          axios.get(`${API}/techniques`, { withCredentials: true }),
          axios.get(`${API}/me/usage`, { withCredentials: true }),
        ]);
        setTechniques(gallery.data.techniques || []);
        setPro(!!gallery.data.pro);
        setTrialEligible(!!usage.data?.trial_eligible);
      } catch (e) {
        toast.error("Couldn't load the technique library.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleClick = (technique) => {
    if (!technique.locked) {
      navigate(`/techniques/${technique.id}`);
      return;
    }
    // Locked → bounce to pricing with a reason so the upgrade modal
    // can show the right copy.
    navigate("/pricing", {
      state: { reason: "technique_locked", techniqueId: technique.id },
    });
  };

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />
      <section className="relative pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F8EEEE] text-[#9B6A6C] text-xs uppercase tracking-[0.18em] mb-5">
              <BookOpen className="h-3.5 w-3.5" />
              Technique library
            </div>
            <h1 className="font-serif italic text-4xl sm:text-5xl text-[#2C2123] mb-4 leading-[1.1]">
              Learn a look, not a whole face.
            </h1>
            <p className="text-[#5C4B51] text-base leading-relaxed">
              Twelve step-by-step technique tutorials — smoky eye, cut
              crease, soft contour, snatched brow — with Trouvé voice
              coaching and AR mirror overlay. No face scan needed. Just
              pick what you want to learn.
            </p>
            {!pro && (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#D5A8A4] to-[#9B6A6C] text-white text-sm font-medium shadow-[0_8px_20px_rgba(155,106,108,0.3)]">
                <Sparkles className="h-4 w-4" />
                {trialEligible
                  ? "Try the full library free for 7 days"
                  : "Unlock all 12 techniques with Pro"}
              </div>
            )}
          </motion.div>

          {/* Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#9B6A6C]">
              <div className="h-8 w-8 border-2 border-[#9B6A6C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {techniques.map((t, i) => (
                <TechniqueCard
                  key={t.id}
                  technique={t}
                  delay={i * 0.04}
                  onClick={() => handleClick(t)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const TechniqueCard = ({ technique, delay, onClick }) => {
  const diff = DIFFICULTY_COPY[technique.difficulty] || DIFFICULTY_COPY.beginner;
  const locked = technique.locked;
  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      onClick={onClick}
      className={`group text-left bg-white border rounded-2xl p-6 transition-all soft-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B6A6C] focus-visible:ring-offset-2 relative overflow-hidden ${
        locked
          ? "border-[#EADDD7] hover:border-[#D5A8A4]"
          : "border-[#EADDD7] hover:border-[#9B6A6C] hover:-translate-y-0.5"
      }`}
      data-testid={`technique-card-${technique.id}`}
    >
      {/* Locked overlay */}
      {locked && (
        <div className="absolute top-4 right-4 z-10">
          <div className="h-9 w-9 rounded-full bg-[#9B6A6C] flex items-center justify-center shadow-lg">
            <Lock className="h-4 w-4 text-white" />
          </div>
        </div>
      )}
      <div className="text-5xl mb-3" aria-hidden="true">
        {technique.hero_emoji}
      </div>
      <h3 className="font-serif italic text-xl text-[#2C2123] mb-1 leading-tight">
        {technique.title}
      </h3>
      <p className="text-[#9B6A6C] text-sm mb-4">{technique.vibe}</p>
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <span
          className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${diff.chip}`}
        >
          {diff.label}
        </span>
        <span className="text-xs text-[#8A7D82] inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {technique.total_minutes} min
        </span>
        <span className="text-xs text-[#8A7D82]">
          · {technique.step_count} steps
        </span>
      </div>
      <div className="inline-flex items-center text-sm text-[#9B6A6C] group-hover:gap-2 gap-1 transition-all">
        {locked ? "Unlock with Pro" : "Open technique"}
        <ChevronRight className="h-4 w-4" />
      </div>
    </motion.button>
  );
};

export default TechniqueGallery;
